import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { MapContainer, TileLayer, CircleMarker, Popup } from 'react-leaflet';
import Layout from '../components/Layout';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { LineChart, Line, ResponsiveContainer, CartesianGrid, XAxis, YAxis, Tooltip } from 'recharts';
import {
  FiGlobe,
  FiRefreshCw,
  FiFilter,
  FiDownload,
  FiAlertTriangle,
  FiKey,
  FiPlus,
  FiTrash2,
  FiX,
  FiShield,
  FiEye
} from 'react-icons/fi';
import 'leaflet/dist/leaflet.css';

function parseTimestampAsUtc(value) {
  if (!value) return null;

  const raw = String(value).trim();
  if (!raw) return null;

  const isoLike = raw.includes(' ') ? raw.replace(' ', 'T') : raw;
  const hasTimezone = /([zZ]|[+-]\d{2}:\d{2})$/.test(isoLike);
  const normalized = hasTimezone ? isoLike : `${isoLike}Z`;
  const parsed = new Date(normalized);
  if (!Number.isNaN(parsed.getTime())) return parsed;

  const fallback = new Date(raw);
  return Number.isNaN(fallback.getTime()) ? null : fallback;
}

function formatDateTime(value) {
  if (!value) return '-';
  const parsed = parseTimestampAsUtc(value);
  if (!parsed || Number.isNaN(parsed.getTime())) return String(value);
  const formatter = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Kolkata',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  });
  return `${formatter.format(parsed)} IST`;
}

function toTitle(text) {
  return String(text || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function MapLoginPopup({ group, onSelectRecord }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const total = group.logins.length;
  const currentLogin = group.logins[currentIndex] || group.logins[0];

  if (!currentLogin) return null;

  const isSecurityPortal =
    currentLogin.metadata?.source === 'security_login' ||
    String(currentLogin.portal_origin || '').includes('security') ||
    String(currentLogin.portal_host || '').includes('security');

  return (
    <div className="text-gray-900 w-64 sm:w-72 font-sans py-0.5 select-text">
      {total > 1 && (
        <div className="flex items-center justify-between border-b border-gray-200 pb-1.5 mb-2 text-xs">
          <span className="font-semibold text-gray-700">
            {group.city || 'Location'} ({currentIndex + 1} of {total})
          </span>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setCurrentIndex((prev) => (prev > 0 ? prev - 1 : total - 1))}
              className="px-2 py-0.5 rounded bg-gray-100 hover:bg-gray-200 text-xs font-bold text-gray-700 cursor-pointer"
              title="Previous login at this location"
            >
              &larr;
            </button>
            <button
              type="button"
              onClick={() => setCurrentIndex((prev) => (prev < total - 1 ? prev + 1 : 0))}
              className="px-2 py-0.5 rounded bg-gray-100 hover:bg-gray-200 text-xs font-bold text-gray-700 cursor-pointer"
              title="Next login at this location"
            >
              &rarr;
            </button>
          </div>
        </div>
      )}

      {/* User Header */}
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-bold text-sm text-gray-900 leading-tight truncate">
            {currentLogin.name || 'Unknown User'}
          </div>
          <div className="text-xs text-gray-500 truncate">
            {currentLogin.email || 'No email'}
          </div>
        </div>
        <span
          className={`shrink-0 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${
            currentLogin.role === 'admin'
              ? 'bg-purple-100 text-purple-800'
              : currentLogin.role === 'manager'
              ? 'bg-blue-100 text-blue-800'
              : 'bg-gray-100 text-gray-800'
          }`}
        >
          {currentLogin.role || 'Staff'}
        </span>
      </div>

      {/* Department & Portal */}
      <div className="mt-2 flex flex-wrap items-center gap-1.5 text-xs text-gray-600">
        {currentLogin.department && (
          <span className="inline-block rounded bg-gray-100 px-1.5 py-0.5 text-[11px] font-medium text-gray-700">
            {currentLogin.department}
          </span>
        )}
        <span
          className={`inline-block rounded px-1.5 py-0.5 text-[11px] font-medium ${
            isSecurityPortal ? 'bg-amber-100 text-amber-900 border border-amber-200' : 'bg-slate-100 text-slate-700'
          }`}
        >
          {isSecurityPortal ? 'Security Portal' : 'Dashboard Login'}
        </span>
      </div>

      {/* Login Timestamp */}
      <div className="mt-2.5 rounded-lg border border-gray-100 bg-gray-50 p-2 text-xs space-y-1">
        <div className="flex items-center justify-between gap-2">
          <span className="text-gray-500 font-medium shrink-0">Time (IST):</span>
          <span className="font-semibold text-gray-900 text-right truncate">
            {formatDateTime(currentLogin.login_at)}
          </span>
        </div>
        <div className="flex items-center justify-between gap-2">
          <span className="text-gray-500 shrink-0">IP Address:</span>
          <span className="font-mono text-[11px] text-gray-700 truncate">{currentLogin.ip_address || '-'}</span>
        </div>
      </div>

      {/* HOD PIN Info */}
      <div className="mt-2">
        {currentLogin.hod_name || currentLogin.hod_pin_used ? (
          <div className="rounded-lg border border-blue-200 bg-blue-50/80 p-2 text-xs">
            <div className="flex items-center justify-between mb-1">
              <span className="font-semibold text-blue-900 flex items-center gap-1">
                <FiKey className="h-3 w-3 text-blue-600" />
                HOD PIN Used
              </span>
              <span className="font-mono font-bold text-xs bg-blue-200 text-blue-900 px-1.5 py-0.5 rounded">
                {currentLogin.hod_pin_used || '••••'}
              </span>
            </div>
            <div className="text-[11px] text-blue-800">
              Approved by: <strong>{currentLogin.hod_name}</strong>
            </div>
          </div>
        ) : (
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-1.5 text-xs text-gray-500 flex items-center gap-1.5">
            <FiKey className="h-3 w-3 text-gray-400" />
            <span>Direct Login (No HOD PIN required)</span>
          </div>
        )}
      </div>

      {/* Location & Status */}
      <div className="mt-2 text-xs text-gray-500 flex items-center justify-between gap-2">
        <span className="truncate">
          📍 {[currentLogin.city, currentLogin.region, currentLogin.country].filter(Boolean).join(', ') || 'Unknown location'}
        </span>
        {currentLogin.is_suspicious ? (
          <span className="shrink-0 font-semibold text-red-600 bg-red-50 border border-red-200 px-1.5 py-0.5 rounded text-[10px]">
            ⚠️ Suspicious
          </span>
        ) : (
          <span className="shrink-0 text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded text-[10px] font-medium">
            ✓ Normal
          </span>
        )}
      </div>

      {onSelectRecord && (
        <button
          type="button"
          onClick={() => onSelectRecord(currentLogin)}
          className="mt-2.5 w-full rounded-lg bg-gray-900 py-1.5 text-center text-xs font-semibold text-white hover:bg-gray-800 transition-colors cursor-pointer"
        >
          View Complete Audit Details
        </button>
      )}
    </div>
  );
}

function LoginActivity() {
  const { user } = useAuth();
  const [records, setRecords] = useState([]);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [selectedUserId, setSelectedUserId] = useState('');
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [suspiciousOnlyExport, setSuspiciousOnlyExport] = useState(false);
  const [checkinSchedule, setCheckinSchedule] = useState(null);
  const [scheduleLoading, setScheduleLoading] = useState(false);

  // HOD PIN management state
  const [hodPins, setHodPins] = useState([]);
  const [hodPinsLoading, setHodPinsLoading] = useState(false);
  const [showPinForm, setShowPinForm] = useState(false);
  const [newPin, setNewPin] = useState({ hod_name: '', department: '', pin: '' });
  const [pinFormError, setPinFormError] = useState('');
  const [pinSaving, setPinSaving] = useState(false);
  const [changePin, setChangePin] = useState({ id: null, pin: '' });

  const canViewAll = user?.role === 'admin' || user?.role === 'manager';

  const fetchHodPins = useCallback(async () => {
    if (!canViewAll) return;
    try {
      setHodPinsLoading(true);
      const res = await api.get('/api/auth/hod-pins');
      setHodPins(res.data || []);
    } catch (_) {}
    finally { setHodPinsLoading(false); }
  }, [canViewAll]);

  useEffect(() => { fetchHodPins(); }, [fetchHodPins]);

  const fetchCheckinSchedule = useCallback(async () => {
    if (!canViewAll) return;
    try {
      setScheduleLoading(true);
      const res = await api.get('/api/auth/checkin-reminder-schedule');
      setCheckinSchedule(res.data || null);
    } catch (err) {
      console.error('Failed to fetch check-in reminder schedule:', err);
      setCheckinSchedule(null);
    } finally {
      setScheduleLoading(false);
    }
  }, [canViewAll]);

  useEffect(() => {
    fetchCheckinSchedule();
  }, [fetchCheckinSchedule]);

  const handleCreatePin = async (e) => {
    e.preventDefault();
    setPinFormError('');
    if (!/^\d{4}$/.test(newPin.pin)) { setPinFormError('PIN must be exactly 4 digits.'); return; }
    if (!newPin.hod_name.trim()) { setPinFormError('HOD Name is required.'); return; }
    try {
      setPinSaving(true);
      await api.post('/api/auth/hod-pins', newPin);
      setNewPin({ hod_name: '', department: '', pin: '' });
      setShowPinForm(false);
      fetchHodPins();
    } catch (err) {
      setPinFormError(err.response?.data?.error || 'Failed to create PIN');
    } finally { setPinSaving(false); }
  };

  const handleDeletePin = async (id) => {
    if (!window.confirm('Delete this HOD PIN? The HOD will no longer be able to log in.')) return;
    try {
      await api.delete(`/api/auth/hod-pins/${id}`);
      fetchHodPins();
    } catch (_) {}
  };

  const handleToggleActive = async (pin) => {
    try {
      await api.put(`/api/auth/hod-pins/${pin.id}`, { is_active: !pin.is_active });
      fetchHodPins();
    } catch (_) {}
  };

  const handleChangePin = async (id) => {
    if (!/^\d{4}$/.test(changePin.pin)) { alert('PIN must be exactly 4 digits.'); return; }
    try {
      await api.put(`/api/auth/hod-pins/${id}/pin`, { pin: changePin.pin });
      setChangePin({ id: null, pin: '' });
      fetchHodPins();
    } catch (err) { alert(err.response?.data?.error || 'Failed to change PIN'); }
  };

  const fetchActivity = async () => {
    try {
      setLoading(true);
      setError('');
      const params = canViewAll && roleFilter ? { role: roleFilter } : {};
      const response = await api.get('/api/auth/login-activity', { params });
      setRecords(response.data?.records || []);
    } catch (fetchError) {
      console.error('Failed to fetch login activity:', fetchError);
      setError(fetchError.response?.data?.error || 'Failed to load login activity');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchActivity();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roleFilter]);

  const fetchAnalytics = async () => {
    try {
      setAnalyticsLoading(true);
      const params = {};
      if (canViewAll && roleFilter) params.role = roleFilter;
      if (selectedUserId) params.userId = selectedUserId;
      const response = await api.get('/api/auth/login-activity/analytics', { params });
      setAnalytics(response.data?.analytics || null);
    } catch (analyticsError) {
      console.error('Failed to fetch login analytics:', analyticsError);
      setAnalytics(null);
    } finally {
      setAnalyticsLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roleFilter, selectedUserId]);

  const exportCsv = async () => {
    try {
      setExporting(true);
      const params = {};
      if (canViewAll && roleFilter) params.role = roleFilter;
      if (selectedUserId) params.userId = selectedUserId;
      if (suspiciousOnlyExport) params.suspiciousOnly = '1';

      const response = await api.get('/api/auth/login-activity/export.csv', {
        params,
        responseType: 'blob'
      });

      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `login-activity-${new Date().toISOString().slice(0, 10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (exportError) {
      console.error('Failed to export login activity CSV:', exportError);
      alert(exportError.response?.data?.error || 'Failed to export CSV');
    } finally {
      setExporting(false);
    }
  };

  const mappedLoginsCount = useMemo(
    () => records.filter((item) => typeof item.latitude === 'number' && typeof item.longitude === 'number').length,
    [records]
  );

  const mapPoints = useMemo(() => {
    const validRecords = records.filter(
      (item) => typeof item.latitude === 'number' && typeof item.longitude === 'number'
    );

    const locationMap = new Map();
    validRecords.forEach((item) => {
      const key = `${Number(item.latitude).toFixed(4)},${Number(item.longitude).toFixed(4)}`;
      if (!locationMap.has(key)) {
        locationMap.set(key, {
          key,
          lat: Number(item.latitude),
          lng: Number(item.longitude),
          city: item.city,
          region: item.region,
          country: item.country,
          has_suspicious: !!item.is_suspicious,
          logins: []
        });
      }
      const group = locationMap.get(key);
      if (item.is_suspicious) group.has_suspicious = true;
      group.logins.push(item);
    });

    const groups = Array.from(locationMap.values());
    groups.forEach((group) => {
      group.logins.sort((a, b) => new Date(b.login_at || 0) - new Date(a.login_at || 0));
    });

    return groups;
  }, [records]);

  const mapCenter = useMemo(() => {
    if (mapPoints.length === 0) return [20.5937, 78.9629];
    return [mapPoints[0].lat, mapPoints[0].lng];
  }, [mapPoints]);

  const usersForFilter = useMemo(() => {
    const uniq = new Map();
    records.forEach((entry) => {
      if (!entry.user_id) return;
      if (uniq.has(entry.user_id)) return;
      uniq.set(entry.user_id, {
        id: entry.user_id,
        label: `${entry.name || 'Unknown'} (${entry.email || 'no-email'})`
      });
    });
    return [...uniq.values()];
  }, [records]);

  const suspiciousRecords = useMemo(
    () => records.filter((row) => !!row.is_suspicious).slice(0, 15),
    [records]
  );

  return (
    <Layout>
      <div className="space-y-6 px-1 py-2">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight">Login Activity Map</h1>
            <p className="text-sm text-gray-500 mt-1">Track where each dashboard profile login happened.</p>
          </div>
          <div className="flex items-center gap-2">
            {canViewAll && (
              <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2">
                <FiFilter className="h-4 w-4 text-gray-500" />
                <select
                  value={roleFilter}
                  onChange={(e) => setRoleFilter(e.target.value)}
                  className="text-sm bg-transparent outline-none"
                >
                  <option value="">All roles</option>
                  <option value="admin">Admin</option>
                  <option value="manager">Manager</option>
                  <option value="department">Department</option>
                  <option value="operator">Operator</option>
                </select>
              </div>
            )}
            <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2">
              <span className="text-xs text-gray-500">Profile</span>
              <select
                value={selectedUserId}
                onChange={(e) => setSelectedUserId(e.target.value)}
                className="text-sm bg-transparent outline-none"
              >
                <option value="">All profiles</option>
                {usersForFilter.map((entry) => (
                  <option key={entry.id} value={entry.id}>
                    {entry.label}
                  </option>
                ))}
              </select>
            </div>
            <label className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs text-gray-600">
              <input
                type="checkbox"
                checked={suspiciousOnlyExport}
                onChange={(e) => setSuspiciousOnlyExport(e.target.checked)}
              />
              CSV suspicious only
            </label>
            <button
              type="button"
              onClick={exportCsv}
              disabled={exporting}
              className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-60"
            >
              <FiDownload className="h-4 w-4" />
              {exporting ? 'Exporting...' : 'Export CSV'}
            </button>
            <button
              type="button"
              onClick={fetchActivity}
              className="inline-flex items-center gap-2 rounded-lg bg-gray-900 px-4 py-2 text-sm font-semibold text-white hover:bg-gray-800"
            >
              <FiRefreshCw className="h-4 w-4" />
              Refresh
            </button>
          </div>
        </div>

        {/* ── HOD PIN MANAGEMENT PANEL ────────────────────────────────────── */}
        {canViewAll && (
          <div className="rounded-xl border border-gray-200 bg-white p-4">
            <div className="mb-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FiShield className="h-4 w-4 text-gray-700" />
                <h2 className="text-base font-semibold text-gray-900">HOD PIN Management</h2>
                <span className="text-xs text-gray-400">({hodPins.length} PINs registered)</span>
              </div>
              <button onClick={() => { setShowPinForm(p => !p); setPinFormError(''); }}
                className="inline-flex items-center gap-1.5 rounded-lg bg-gray-900 px-3 py-1.5 text-xs font-semibold text-white hover:bg-gray-800">
                <FiPlus className="h-3.5 w-3.5" />
                Add HOD PIN
              </button>
            </div>

            {showPinForm && (
              <form onSubmit={handleCreatePin} className="mb-4 rounded-xl border border-blue-100 bg-blue-50 p-4 space-y-3">
                <h3 className="text-sm font-semibold text-blue-900">New HOD PIN</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">HOD Name *</label>
                    <input type="text" placeholder="e.g. Priya — Spa Head" value={newPin.hod_name}
                      onChange={e => setNewPin(p => ({ ...p, hod_name: e.target.value }))} required
                      className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-300" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Department</label>
                    <select value={newPin.department}
                      onChange={e => setNewPin(p => ({ ...p, department: e.target.value }))}
                      className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-300 bg-white">
                      <option value="">— Select Department —</option>
                      <option value="Admin">Admin</option>
                      <option value="Manager">Manager</option>
                      <option value="Housekeeping Service">Housekeeping Service</option>
                      <option value="Maintenance Assistance">Maintenance Assistance</option>
                      <option value="IT Support">IT Support</option>
                      <option value="Front Office">Front Office</option>
                      <option value="SPA Appointment">SPA Appointment</option>
                      <option value="Yoga &amp; Wellness">Yoga &amp; Wellness</option>
                      <option value="Salon">Salon</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">4-Digit PIN *</label>
                    <input type="password" placeholder="••••" maxLength={4} value={newPin.pin}
                      onChange={e => setNewPin(p => ({ ...p, pin: e.target.value.replace(/\D/g, '').slice(0, 4) }))} required
                      className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-gray-300" />
                  </div>
                </div>
                {pinFormError && <p className="text-xs text-red-600">{pinFormError}</p>}
                <div className="flex gap-2">
                  <button type="submit" disabled={pinSaving}
                    className="rounded-lg bg-gray-900 px-4 py-2 text-xs font-semibold text-white hover:bg-gray-800 disabled:opacity-50">
                    {pinSaving ? 'Saving...' : 'Create PIN'}
                  </button>
                  <button type="button" onClick={() => setShowPinForm(false)}
                    className="rounded-lg border border-gray-200 px-4 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50">
                    Cancel
                  </button>
                </div>
              </form>
            )}

            {hodPinsLoading ? (
              <p className="text-sm text-gray-500">Loading HOD PINs...</p>
            ) : hodPins.length === 0 ? (
              <p className="text-sm text-gray-400">No HOD PINs registered yet. Add one above.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm divide-y divide-gray-100">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">HOD Name</th>
                      <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Department</th>
                      <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">PIN</th>
                      <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Status</th>
                      <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Created</th>
                      <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 bg-white">
                    {hodPins.map(pin => (
                      <tr key={pin.id}>
                        <td className="px-4 py-2 font-medium text-gray-900">{pin.hod_name}</td>
                        <td className="px-4 py-2 text-gray-600">{pin.department || '—'}</td>
                        <td className="px-4 py-2">
                          {changePin.id === pin.id ? (
                            <div className="flex items-center gap-1">
                              <input type="password" maxLength={4} placeholder="••••" value={changePin.pin}
                                onChange={e => setChangePin(p => ({ ...p, pin: e.target.value.replace(/\D/g, '').slice(0, 4) }))}
                                className="w-20 rounded border border-gray-200 px-2 py-1 text-xs font-mono" />
                              <button onClick={() => handleChangePin(pin.id)}
                                className="rounded bg-gray-900 px-2 py-1 text-xs text-white">Save</button>
                              <button onClick={() => setChangePin({ id: null, pin: '' })}
                                className="text-gray-400 hover:text-gray-600"><FiX className="h-3.5 w-3.5" /></button>
                            </div>
                          ) : (
                            <button onClick={() => setChangePin({ id: pin.id, pin: '' })}
                              className="inline-flex items-center gap-1 rounded border border-gray-200 px-2 py-0.5 text-xs text-gray-600 hover:bg-gray-50">
                              <FiKey className="h-3 w-3" /> <span className={pin.raw_pin ? "font-mono font-medium" : ""}>{pin.raw_pin || '••••'}</span>
                            </button>
                          )}
                        </td>
                        <td className="px-4 py-2">
                          <button onClick={() => handleToggleActive(pin)}
                            className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${
                              pin.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                            }`}>
                            {pin.is_active ? 'Active' : 'Inactive'}
                          </button>
                        </td>
                        <td className="px-4 py-2 text-gray-500 text-xs">{new Date(pin.created_at).toLocaleDateString('en-IN')}</td>
                        <td className="px-4 py-2">
                          <button onClick={() => handleDeletePin(pin.id)}
                            className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded">
                            <FiTrash2 className="h-3.5 w-3.5" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {canViewAll && (
          <div className="rounded-xl border border-gray-200 bg-white p-4">
            <div className="mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div>
                <h2 className="text-base font-semibold text-gray-900">Check-in Reminder Schedule Validation</h2>
                <p className="text-xs text-gray-500 mt-1">
                  Preview of reminders that should be sent from shift logic for today and tomorrow.
                </p>
              </div>
              <button
                type="button"
                onClick={fetchCheckinSchedule}
                className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50"
              >
                <FiRefreshCw className={`h-3.5 w-3.5 ${scheduleLoading ? 'animate-spin' : ''}`} />
                Refresh Schedule
              </button>
            </div>

            <div className="mb-3 grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                <p className="text-gray-500 uppercase tracking-wide">Daily Reminder Time</p>
                <p className="mt-1 text-sm font-semibold text-gray-900">
                  {checkinSchedule?.settings?.reminder_time_ist || '-'} IST
                </p>
              </div>
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                <p className="text-gray-500 uppercase tracking-wide">Recipients</p>
                <p className="mt-1 text-sm font-semibold text-gray-900">
                  {checkinSchedule?.settings?.reminder_recipients === 'hod_only' ? 'HOD Only' : 'Staff + HOD'}
                </p>
              </div>
            </div>

            {scheduleLoading ? (
              <p className="text-sm text-gray-500">Loading schedule...</p>
            ) : (
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                {[
                  { key: 'today', label: 'Today' },
                  { key: 'tomorrow', label: 'Tomorrow' }
                ].map((bucket) => {
                  const dayBlock = checkinSchedule?.[bucket.key];
                  const entries = Array.isArray(dayBlock?.entries) ? dayBlock.entries : [];
                  return (
                    <div key={bucket.key} className="rounded-lg border border-gray-200 overflow-hidden">
                      <div className="bg-gray-50 px-3 py-2 border-b border-gray-200">
                        <p className="text-sm font-semibold text-gray-900">
                          {bucket.label} ({dayBlock?.date || '-'})
                        </p>
                      </div>
                      <div className="max-h-80 overflow-auto">
                        <table className="min-w-full text-xs">
                          <thead className="bg-white sticky top-0 z-10 border-b border-gray-100">
                            <tr className="text-gray-500">
                              <th className="px-3 py-2 text-left font-semibold">Reminder At (IST)</th>
                              <th className="px-3 py-2 text-left font-semibold">Staff</th>
                              <th className="px-3 py-2 text-left font-semibold">Type</th>
                            </tr>
                          </thead>
                          <tbody>
                            {entries.length === 0 ? (
                              <tr>
                                <td colSpan={3} className="px-3 py-4 text-center text-gray-500">
                                  No reminders scheduled
                                </td>
                              </tr>
                            ) : (
                              entries.map((entry) => (
                                <tr key={`${bucket.key}-${entry.staff_id}-${entry.reminder_time}`} className="border-t border-gray-100">
                                  <td className="px-3 py-2 text-gray-700">{entry.scheduled_at_ist}</td>
                                  <td className="px-3 py-2">
                                    <div className="font-medium text-gray-900">{entry.staff_name}</div>
                                    <div className="text-gray-500">{entry.department} {entry.is_hod ? '(HOD)' : ''}</div>
                                  </td>
                                  <td className="px-3 py-2 text-gray-700">
                                    Daily Check-in Reminder
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {error && <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="rounded-xl border border-gray-200 bg-white p-4">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Total logins</p>
            <p className="mt-1 text-2xl font-bold text-gray-900">{records.length}</p>
          </div>
          <div className="rounded-xl border border-gray-200 bg-white p-4">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Mapped logins</p>
            <p className="mt-1 text-2xl font-bold text-gray-900">
              {mappedLoginsCount}{' '}
              <span className="text-xs font-normal text-gray-500">
                ({mapPoints.length} location{mapPoints.length === 1 ? '' : 's'})
              </span>
            </p>
          </div>
          <div className="rounded-xl border border-gray-200 bg-white p-4">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Unresolved IPs</p>
            <p className="mt-1 text-2xl font-bold text-gray-900">{Math.max(records.length - mappedLoginsCount, 0)}</p>
          </div>
        </div>

        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
          <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-amber-800">
            <FiAlertTriangle className="h-4 w-4" />
            Suspicious Login Alerts
          </div>
          {suspiciousRecords.length === 0 ? (
            <p className="text-sm text-amber-700">No suspicious logins detected in current results.</p>
          ) : (
            <div className="space-y-2">
              {suspiciousRecords.map((item) => (
                <div key={item.id} className="rounded-lg border border-amber-200 bg-white px-3 py-2 text-sm">
                  <div className="font-semibold text-gray-900">
                    {item.name || 'Unknown'} ({item.email || '-'})
                  </div>
                  <div className="text-gray-600">
                    {item.city || '-'}, {item.country || '-'} | {formatDateTime(item.login_at)}
                  </div>
                  <div className="text-amber-700 text-xs">
                    Reasons: {(item.suspicious_reasons || []).join(', ') || 'flagged'}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-base font-semibold text-gray-900">Per-User Timeline & Session Analytics</h2>
            {analyticsLoading && <span className="text-xs text-gray-500">Loading analytics...</span>}
          </div>

          {!analytics && !analyticsLoading && (
            <p className="text-sm text-gray-500">No analytics data available for selected filters.</p>
          )}

          {analytics && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                <div className="rounded-lg border border-gray-200 p-3">
                  <p className="text-xs text-gray-500">Estimated sessions</p>
                  <p className="text-xl font-bold text-gray-900">{analytics.estimated_sessions}</p>
                </div>
                <div className="rounded-lg border border-gray-200 p-3">
                  <p className="text-xs text-gray-500">Unique devices</p>
                  <p className="text-xl font-bold text-gray-900">{analytics.unique_devices}</p>
                </div>
                <div className="rounded-lg border border-gray-200 p-3">
                  <p className="text-xs text-gray-500">Unique countries</p>
                  <p className="text-xl font-bold text-gray-900">{analytics.unique_countries}</p>
                </div>
                <div className="rounded-lg border border-gray-200 p-3">
                  <p className="text-xs text-gray-500">Suspicious logins</p>
                  <p className="text-xl font-bold text-gray-900">{analytics.suspicious_logins}</p>
                </div>
              </div>

              <div className="h-[260px] rounded-lg border border-gray-200 p-2">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={analytics.timeline || []}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                    <Tooltip />
                    <Line type="monotone" dataKey="count" stroke="#111827" strokeWidth={2} dot={{ r: 2 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </>
          )}
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <div className="mb-3 flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm font-semibold text-gray-700">
              <FiGlobe className="h-4 w-4" />
              Login Locations
            </div>
            <span className="text-xs text-gray-500">
              Click on any pin to inspect the login, timestamp, and PIN used.
            </span>
          </div>
          <div className="h-[400px] overflow-hidden rounded-lg border border-gray-200">
            <MapContainer center={mapCenter} zoom={mapPoints.length > 0 ? 5 : 4} className="h-full w-full">
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              {mapPoints.map((point) => (
                <CircleMarker
                  key={point.key}
                  center={[point.lat, point.lng]}
                  radius={point.logins.length > 1 ? 10 : 7}
                  pathOptions={{
                    color: point.has_suspicious ? '#dc2626' : '#111827',
                    fillColor: point.has_suspicious ? '#ef4444' : '#1f2937',
                    fillOpacity: 0.85,
                    weight: 2
                  }}
                >
                  <Popup minWidth={280} maxWidth={360}>
                    <MapLoginPopup group={point} onSelectRecord={setSelectedRecord} />
                  </Popup>
                </CircleMarker>
              ))}
            </MapContainer>
          </div>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
            <div>
              <h2 className="text-base font-semibold text-gray-900">Login History</h2>
              <p className="text-xs text-gray-500 mt-0.5">Click any row to open the complete audit breakdown.</p>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Profile</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">HOD</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Role</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Location</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">IP</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Portal</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Risk</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Login Time</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {!loading && records.length === 0 && (
                  <tr>
                    <td className="px-4 py-6 text-center text-gray-500" colSpan={9}>
                      No login activity found.
                    </td>
                  </tr>
                )}
                {loading && (
                  <tr>
                    <td className="px-4 py-6 text-center text-gray-500" colSpan={9}>
                      Loading login activity...
                    </td>
                  </tr>
                )}
                {!loading &&
                  records.map((entry) => (
                    <tr
                      key={entry.id}
                      onClick={() => setSelectedRecord(entry)}
                      className="cursor-pointer hover:bg-blue-50/40 transition-colors"
                    >
                      <td className="px-4 py-3 align-top">
                        <div className="font-medium text-gray-900">{entry.name || '-'}</div>
                        <div className="text-xs text-gray-500">{entry.email || '-'}</div>
                      </td>
                      <td className="px-4 py-3 align-top">
                        {entry.hod_name ? (
                          <div>
                            <div className="font-medium text-gray-900 text-xs">{entry.hod_name}</div>
                            {entry.hod_pin_used && (
                              <span className="inline-flex items-center gap-1 mt-0.5 rounded bg-gray-100 px-1.5 py-0.5 text-xs font-mono text-gray-600">
                                <FiKey className="h-2.5 w-2.5" />{entry.hod_pin_used}
                              </span>
                            )}
                          </div>
                        ) : <span className="text-gray-400 text-xs">—</span>}
                      </td>
                      <td className="px-4 py-3 align-top">{toTitle(entry.role || '-')}</td>
                      <td className="px-4 py-3 align-top">
                        <div>{entry.city || '-'}, {entry.region || '-'}</div>
                        <div className="text-xs text-gray-500">{entry.country || '-'}</div>
                      </td>
                      <td className="px-4 py-3 align-top font-mono text-xs text-gray-600">{entry.ip_address || '-'}</td>
                      <td className="px-4 py-3 align-top">
                        <div className="text-xs font-medium text-gray-800">
                          {entry.metadata?.source === 'security_login' || String(entry.portal_origin || '').includes('security')
                            ? 'Security Portal'
                            : 'Dashboard'}
                        </div>
                        <div className="text-[11px] text-gray-400">{entry.portal_host || '-'}</div>
                      </td>
                      <td className="px-4 py-3 align-top">
                        {entry.is_suspicious ? (
                          <span className="inline-flex rounded-full bg-amber-100 px-2 py-1 text-xs font-semibold text-amber-800">
                            Suspicious
                          </span>
                        ) : (
                          <span className="inline-flex rounded-full bg-green-100 px-2 py-1 text-xs font-semibold text-green-800">
                            Normal
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 align-top text-xs text-gray-700">{formatDateTime(entry.login_at)}</td>
                      <td className="px-4 py-3 align-top">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedRecord(entry);
                          }}
                          className="inline-flex items-center gap-1 rounded-lg border border-gray-200 bg-white px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-100 shadow-sm"
                        >
                          <FiEye className="h-3 w-3 text-gray-500" /> View
                        </button>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Full Audit Details Modal */}
        {selectedRecord && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
            <div className="relative w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl space-y-4 max-h-[90vh] overflow-y-auto">
              <div className="flex items-start justify-between border-b border-gray-100 pb-3">
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Sign-in Audit Details</h3>
                  <p className="text-xs text-gray-500 mt-0.5">Comprehensive audit log for this session</p>
                </div>
                <button
                  type="button"
                  onClick={() => setSelectedRecord(null)}
                  className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                >
                  <FiX className="h-5 w-5" />
                </button>
              </div>

              <div className="space-y-3 text-sm">
                {/* Profile Card */}
                <div className="rounded-xl border border-gray-200 bg-gray-50 p-3.5 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-gray-500 uppercase">Staff Profile</span>
                    <span className="rounded-full bg-gray-900 px-2.5 py-0.5 text-xs font-semibold text-white">
                      {toTitle(selectedRecord.role || 'Staff')}
                    </span>
                  </div>
                  <div>
                    <div className="text-base font-bold text-gray-900">{selectedRecord.name || 'Unknown'}</div>
                    <div className="text-xs text-gray-600">{selectedRecord.email || '-'}</div>
                    {selectedRecord.department && (
                      <div className="text-xs text-gray-500 mt-1">
                        Department: <strong>{selectedRecord.department}</strong>
                      </div>
                    )}
                  </div>
                </div>

                {/* Time and Portal */}
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div className="rounded-xl border border-gray-200 p-3 bg-white">
                    <p className="text-gray-500 font-medium">Login Time (IST)</p>
                    <p className="mt-1 font-semibold text-gray-900 text-sm">{formatDateTime(selectedRecord.login_at)}</p>
                  </div>
                  <div className="rounded-xl border border-gray-200 p-3 bg-white">
                    <p className="text-gray-500 font-medium">Portal / Flow</p>
                    <p className="mt-1 font-semibold text-gray-900 text-sm">
                      {selectedRecord.metadata?.source === 'security_login' ||
                      String(selectedRecord.portal_origin || '').includes('security')
                        ? 'Security Portal Login'
                        : 'Dashboard Login'}
                    </p>
                  </div>
                </div>

                {/* HOD PIN Verification */}
                <div className="rounded-xl border border-blue-200 bg-blue-50/70 p-3.5 space-y-1.5">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-blue-900 flex items-center gap-1.5">
                      <FiKey className="h-4 w-4 text-blue-700" />
                      HOD Verification & PIN
                    </span>
                    {selectedRecord.hod_pin_used ? (
                      <span className="font-mono font-bold text-xs bg-blue-200 text-blue-900 px-2 py-0.5 rounded">
                        PIN: {selectedRecord.hod_pin_used}
                      </span>
                    ) : (
                      <span className="text-xs text-gray-500 font-medium">No PIN required</span>
                    )}
                  </div>
                  {selectedRecord.hod_name ? (
                    <p className="text-xs text-blue-800">
                      Verified & Authorized by: <strong>{selectedRecord.hod_name}</strong>
                    </p>
                  ) : (
                    <p className="text-xs text-gray-600">
                      This login did not require an HOD PIN (Direct administrative or operator session).
                    </p>
                  )}
                </div>

                {/* Location & GPS */}
                <div className="rounded-xl border border-gray-200 p-3.5 bg-white space-y-2 text-xs">
                  <span className="font-semibold text-gray-900 flex items-center gap-1.5">
                    <FiGlobe className="h-4 w-4 text-gray-700" />
                    Location & Coordinates
                  </span>
                  <div className="grid grid-cols-2 gap-2 text-gray-700">
                    <div>
                      <span className="text-gray-500">City / Region:</span>{' '}
                      <strong>{[selectedRecord.city, selectedRecord.region].filter(Boolean).join(', ') || '-'}</strong>
                    </div>
                    <div>
                      <span className="text-gray-500">Country:</span>{' '}
                      <strong>
                        {selectedRecord.country || '-'} ({selectedRecord.country_code || '-'})
                      </strong>
                    </div>
                    <div>
                      <span className="text-gray-500">Coordinates:</span>{' '}
                      <span className="font-mono">
                        {selectedRecord.latitude != null && selectedRecord.longitude != null
                          ? `${Number(selectedRecord.latitude).toFixed(5)}, ${Number(selectedRecord.longitude).toFixed(5)}`
                          : 'Not mapped'}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500">Timezone:</span> <span>{selectedRecord.timezone || '-'}</span>
                    </div>
                  </div>
                  {selectedRecord.metadata?.location_source && (
                    <div className="text-[11px] text-gray-500 pt-1 border-t border-gray-100">
                      Source: <span className="font-medium text-gray-700">{selectedRecord.metadata.location_source}</span>
                    </div>
                  )}
                </div>

                {/* Network & Device */}
                <div className="rounded-xl border border-gray-200 p-3.5 bg-white space-y-2 text-xs">
                  <span className="font-semibold text-gray-900 flex items-center gap-1.5">
                    <FiShield className="h-4 w-4 text-gray-700" />
                    Device & Network
                  </span>
                  <div className="space-y-1 text-gray-700">
                    <div className="flex justify-between">
                      <span className="text-gray-500">IP Address:</span>
                      <span className="font-mono">{selectedRecord.ip_address || '-'}</span>
                    </div>
                    {selectedRecord.isp && (
                      <div className="flex justify-between">
                        <span className="text-gray-500">ISP:</span>
                        <span>{selectedRecord.isp}</span>
                      </div>
                    )}
                    {selectedRecord.device_fingerprint && (
                      <div className="flex justify-between">
                        <span className="text-gray-500">Device Fingerprint:</span>
                        <span className="font-mono text-[11px] truncate max-w-[240px]">{selectedRecord.device_fingerprint}</span>
                      </div>
                    )}
                    {selectedRecord.user_agent && (
                      <div className="pt-1 text-[11px] text-gray-500 break-words line-clamp-2">
                        User Agent: {selectedRecord.user_agent}
                      </div>
                    )}
                  </div>
                </div>

                {/* Risk / Suspicious Flag */}
                <div
                  className={`rounded-xl border p-3 text-xs ${
                    selectedRecord.is_suspicious
                      ? 'border-amber-300 bg-amber-50 text-amber-900'
                      : 'border-green-200 bg-green-50 text-green-900'
                  }`}
                >
                  <div className="font-semibold flex items-center gap-1.5">
                    <FiAlertTriangle
                      className={`h-4 w-4 ${selectedRecord.is_suspicious ? 'text-amber-700' : 'text-green-700'}`}
                    />
                    {selectedRecord.is_suspicious ? 'Suspicious Login Flagged' : 'Normal Risk Login'}
                  </div>
                  {selectedRecord.is_suspicious && selectedRecord.suspicious_reasons?.length > 0 && (
                    <p className="mt-1 text-xs text-amber-800">
                      Reasons: <strong>{selectedRecord.suspicious_reasons.join(', ')}</strong>
                    </p>
                  )}
                </div>
              </div>

              <div className="flex justify-end pt-2">
                <button
                  type="button"
                  onClick={() => setSelectedRecord(null)}
                  className="rounded-xl bg-gray-900 px-5 py-2 text-xs font-semibold text-white hover:bg-gray-800"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default LoginActivity;
